import { reactive } from 'vue'
import { useChatSocket } from './useChatSocket'

/**
 * Client cache for rich user hover-cards (GET /api/chat/users/:id/card).
 *
 * - Map cache + in-flight dedup so hovering the same row twice (or two rows for
 *   the same user) issues a single request.
 * - Short TTL so a card re-opened minutes later refreshes friendship/bio.
 * - LIVE patching from the WS so an OPEN card stays current without a refetch:
 *     · `presence.update` → status / lastSeen
 *     · `profile.update`  → name (resolved displayName) / customStatus /
 *                           openForCollabs / bio (bio isn't broadcast today but
 *                           we tolerate it for forward-compat)
 *   This mirrors usePresence's two-channel handling so the card and the list
 *   never disagree.
 */

export interface UserCard {
    id: string
    name: string
    avatar: string | null
    role: string
    openForCollabs?: boolean
    bio: string | null
    customStatus: string | null
    status: string
    lastSeen: string | null
    friendship: { status: 'pending' | 'accepted' | 'blocked'; direction: 'incoming' | 'outgoing' | null; id: string } | null
}

const TTL_MS = 60_000

const cards = reactive(new Map<string, UserCard>())
const fetchedAt = new Map<string, number>()
const inflight = new Map<string, Promise<UserCard | null>>()

let wired = false
function wire() {
    if (wired || typeof window === 'undefined') return
    wired = true
    const { on } = useChatSocket()

    on('presence.update', (frame) => {
        const c = cards.get(frame.userId)
        if (!c) return
        const raw = String(frame.status || '')
        if (raw === 'online' || raw === 'afk' || raw === 'dnd') {
            c.status = raw
            c.lastSeen = null
        } else {
            c.status = 'offline'
            if (frame.lastSeen) c.lastSeen = frame.lastSeen
        }
    })

    on('profile.update', (frame) => {
        if (!frame?.userId || !frame?.patch) return
        const c = cards.get(frame.userId)
        if (!c) return
        const p = frame.patch
        if (typeof p.openForCollabs === 'boolean') c.openForCollabs = p.openForCollabs
        if (typeof p.name === 'string' && p.name) c.name = p.name
        if ('customStatus' in p) c.customStatus = p.customStatus || null
        if ('bio' in p) c.bio = p.bio || null
    })
}

async function getCard(userId: string, opts?: { force?: boolean }): Promise<UserCard | null> {
    wire()
    if (!userId) return null
    const now = Date.now()
    const cached = cards.get(userId)
    if (cached && !opts?.force && now - (fetchedAt.get(userId) || 0) < TTL_MS) return cached

    const existing = inflight.get(userId)
    if (existing && !opts?.force) return existing

    const p = $fetch<{ card: UserCard }>(`/api/chat/users/${userId}/card`)
        .then((res) => {
            // Merge into the existing reactive entry if present. A WS frame
            // (presence.update / profile.update) may have live-patched the card
            // WHILE this fetch was in flight; a blind Object.assign would clobber
            // those fresher values with the now-stale response. Re-apply the
            // live-patched public fields LAST so they win. (status/lastSeen are
            // intentionally refreshed from the response — the fetch is
            // authoritative for them and WS re-patches them afterwards anyway.)
            const prev = cards.get(userId)
            if (prev) {
                const livePatched = {
                    name: prev.name,
                    customStatus: prev.customStatus,
                    openForCollabs: prev.openForCollabs,
                    bio: prev.bio,
                }
                Object.assign(prev, res.card, livePatched)
            } else {
                cards.set(userId, res.card)
            }
            fetchedAt.set(userId, Date.now())
            return cards.get(userId) || null
        })
        .catch(() => null)
        .finally(() => { inflight.delete(userId) })

    inflight.set(userId, p)
    return p
}

function peekCard(userId: string): UserCard | null {
    return cards.get(userId) || null
}

export function useUserCards() {
    wire()
    return { getCard, peekCard, cards }
}
